import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { clearSession, refreshUser } from '../actions/userActions'
import { getStoredUserInfo, hasRefreshToken, hasUsableSession } from '../utils/authSession'

// Shared session-gate logic: read the stored session, refresh it via the
// refresh token when the access token is expired (or close to it), and fall
// back to "unauthenticated" otherwise. Originally lived inline in
// ProtectedRoute; extracted so any screen that needs the same gate (e.g. the
// share-link landing screen, which cannot use ProtectedRoute because it must
// not redirect away and lose the token) can reuse it.
function useSessionGate() {
  const dispatch = useDispatch()
  const [status, setStatus] = useState('checking') // 'checking' | 'authenticated' | 'unauthenticated'

  // Re-run the gate whenever redux's userInfo changes, so a login/refresh
  // that happens while this hook is mounted (e.g. an embedded login modal)
  // is picked up without a page reload.
  const reduxUserInfo = useSelector(state => state.userLogin?.userInfo)

  const checkSession = useCallback(async () => {
    const userObject = getStoredUserInfo()

    if (hasUsableSession(userObject)) {
      setStatus('authenticated')
      return
    }

    if (!hasRefreshToken(userObject)) {
      dispatch(clearSession())
      setStatus('unauthenticated')
      return
    }

    try {
      const res = await axios.post('api/user/token/refresh/', {
        refresh: userObject.refresh,
      })

      if (res.status === 200) {
        dispatch(refreshUser({ ...userObject, ...res.data }))
        setStatus('authenticated')
      } else {
        dispatch(clearSession())
        setStatus('unauthenticated')
      }
    } catch (error) {
      dispatch(clearSession())
      setStatus('unauthenticated')
    }
  }, [dispatch])

  // Only the FIRST check shows 'checking'. Later re-checks keep the current
  // status until they resolve, on purpose: ProtectedRoute unmounts its
  // children while checking, and `refreshUser` hands redux a brand-new
  // userInfo object on every token refresh. Flipping back to 'checking' on
  // each of those would remount the entire guarded screen mid-session —
  // which on DashboardDetailsScreen means throwing away unsaved edits.
  const hasCheckedOnce = useRef(false)

  useEffect(() => {
    if (!hasCheckedOnce.current) {
      setStatus('checking')
    }
    checkSession()
      .catch(() => setStatus('unauthenticated'))
      .finally(() => { hasCheckedOnce.current = true })
  }, [checkSession, reduxUserInfo])

  return {
    status,
    isChecking: status === 'checking',
    isAuthenticated: status === 'authenticated',
    // Lets a caller force a re-check (e.g. after an API call comes back 401
    // mid-flow, to fall back to the signed-out state cleanly).
    recheck: checkSession,
  }
}

export default useSessionGate
